import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';

function ResultPanel({ result }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  
  useEffect(() => {
    if (result) {
      setIsVisible(true);
      setIsCollapsed(false);
      
      // Track result view
      ReactGA.event({
        category: 'Result',
        action: 'View Result',
        label: result.eligible ? 'Eligible' : (result.qualification ? 'Partially Eligible' : 'Not Eligible')
      });
    } else {
      setIsVisible(false);
    }
  }, [result]);
  
  if (!isVisible || !result) return null;
  
  // Determine the correct icon and color based on result
  const getStatusIcon = () => {
    if (result.eligible) {
      return '✅';
    } else if (result.qualification) {
      // Partially eligible - eligible for a lesser qualification
      return '✓';
    } else {
      return '⚠️';
    }
  };
  
  const getHeaderColor = () => {
    if (result.eligible) {
      return 'bg-[#059669]'; // Green with better contrast
    } else if (result.qualification) {
      // Partially eligible - amber with better contrast
      return 'bg-[#F59E0B]';
    } else {
      return 'bg-[#DC2626]'; // Red with better contrast
    }
  };
  
  const getHeaderTitle = () => {
    if (result.eligible) {
      return 'Eligible for Award';
    } else if (result.qualification) {
      return `Eligible for ${result.qualification}`;
    } else {
      return 'Not Eligible';
    }
  };

  // Helper function for text color based on background
  const getTextColor = () => {
    if (result.qualification) {
      return 'text-black'; // Black text on amber background for better contrast
    } 
    return 'text-white'; // White text on green or red
  };
  
  const handleCollapse = () => {
    // Track panel collapse/expand
    ReactGA.event({
      category: 'UI',
      action: isCollapsed ? 'Expand Result Panel' : 'Collapse Result Panel'
    });
    
    setIsCollapsed(!isCollapsed);
  };
  
  const handleDismiss = () => {
    // Track dismiss action
    ReactGA.event({
      category: 'UI',
      action: 'Dismiss Result'
    });
    
    setIsVisible(false);
  };
  
  const handleFeedback = (isPositive) => {
    // Track feedback
    ReactGA.event({
      category: 'Feedback',
      action: isPositive ? 'Thumbs Up' : 'Thumbs Down',
      label: result.eligible ? 'Eligible' : (result.qualification ? 'Partially Eligible' : 'Not Eligible')
    });
    
    setFeedbackGiven(true);
    
    // Hide feedback after 2 seconds
    setTimeout(() => {
      setFeedbackGiven(false);
    }, 2000);
  };
  
  const handleContactUs = () => {
    // Track contact us click
    ReactGA.event({
      category: 'Contact',
      action: 'Contact Us Click',
      label: 'Result Panel'
    });
    
    // Open Pearson support page
    window.open('https://support.pearson.com/getsupport/s/contactsupport', '_blank');
  };
  
  return (
    <div className={`fixed top-5 right-5 z-50 modern-card transition-all duration-300 ${
      isCollapsed ? 'w-16 h-16' : 'w-96'
    }`}>
      <div className={`${getHeaderColor()} ${getTextColor()} p-4 rounded-t-xl flex justify-between items-center`}>
        <div className={`flex items-center ${isCollapsed ? 'hidden' : ''}`}>
          <span className="text-2xl mr-3">{getStatusIcon()}</span>
          <div>
            <h3 className="font-semibold text-base">{getHeaderTitle()}</h3>
            {result.qualification && (
              <p className="text-xs opacity-90 mt-1">{result.qualification}</p>
            )}
          </div>
        </div>
        {isCollapsed && (
          <div className="flex items-center justify-center w-full">
            <span className="text-2xl">{getStatusIcon()}</span>
          </div>
        )}
        <button 
          onClick={handleCollapse} 
          className="p-2 hover:bg-black hover:bg-opacity-20 rounded-full focus:outline-none transition-colors"
          aria-label={isCollapsed ? "Expand result panel" : "Collapse result panel"}
        >
          {isCollapsed ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
      </div>
      
      {!isCollapsed && (
        <div className="p-4 bg-white rounded-b-xl">
          <div className="space-y-3">
            <p className="text-sm text-grey-700 leading-relaxed">{result.message}</p>
            
            {result.alternativeMessage && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start">
                  <span className="text-amber-500 mr-2 flex-shrink-0">💡</span>
                  <p className="text-xs text-amber-800 leading-relaxed">{result.alternativeMessage}</p>
                </div>
              </div>
            )}
            
            {(result.eligible || result.qualification) && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2 flex-shrink-0">ℹ️</span>
                  <p className="text-xs text-blue-800 leading-relaxed">
                    Remember to check with your examination officer for the final verification of your eligibility.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Contact Us Section */}
          <div className="mt-4 pt-3 border-t border-grey-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-grey-600">Need help with your qualification?</p>
              <button 
                onClick={handleContactUs}
                className="text-xs bg-[#00B2A9] hover:bg-[#008F88] text-white py-1 px-3 rounded-full transition-colors font-medium flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Contact Us
              </button>
            </div>
          </div>
          
          {/* Feedback Section */}
          <div className="mt-3 pt-3 border-t border-grey-200">
            {!feedbackGiven ? (
              <div className="flex items-center justify-between">
                <p className="text-xs text-grey-600">Was this helpful?</p>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleFeedback(true)}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50 p-1 rounded transition-colors"
                    aria-label="This was helpful"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M7.493 18.75c-.425 0-.82-.236-.975-.632A7.48 7.48 0 016 15.375c0-1.75.599-3.358 1.602-4.634.151-.192.373-.309.6-.397.473-.183.89-.514 1.212-.924a9.042 9.042 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75 2.25 2.25 0 012.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558-.629 1.08-1.06 1.533l-.149.15c-.16.16-.33.317-.514.463 1.42.384 2.522 1.400 2.522 2.186 0 .996-.506 1.70-.944 2.218a3.825 3.825 0 01-.957.832c-.95.564-1.54 1.555-1.54 2.673v.05c0 .273-.035.54-.102.798-.315 1.195-1.387 2.027-2.686 2.027z"/>
                    </svg>
                  </button>
                  <button 
                    onClick={() => handleFeedback(false)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                    aria-label="This was not helpful"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M15.73 5.25h1.035A7.465 7.465 0 0118 9.375a7.465 7.465 0 01-1.235 4.125h-.148c-.806 0-1.534.446-2.031 1.08a9.04 9.04 0 01-2.861 2.4c-.723.384-1.35.956-1.653 1.715a4.498 4.498 0 00-.322 1.672V21a.75.75 0 01-.75.75 2.25 2.25 0 01-2.25-2.25c0-1.152.26-2.243.723-3.218C7.74 15.724 7.366 15.202 7.366 14.625c0-.996.506-1.70.944-2.218a3.825 3.825 0 01.957-.832C10.217 10.811 10.807 9.82 10.807 8.702v-.05c0-.273.035-.541.102-.798.315-1.195 1.387-2.027 2.686-2.027h.9c.825 0 1.506.603 1.506 1.348 0 .729-.78 1.25-1.506 1.25h-.9c-.825 0-1.506.603-1.506 1.348 0 .729.78 1.25 1.506 1.25h2.04z"/>
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-1">
                <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Thank you for your feedback!
                </p>
              </div>
            )}
          </div>
          
          <div className="flex gap-2 mt-3 pt-3 border-t border-grey-200">
            <button 
              onClick={handleDismiss}
              className="flex-1 text-sm bg-grey-100 hover:bg-grey-200 text-grey-700 py-2 px-3 rounded-lg transition-colors font-medium"
            >
              Dismiss
            </button>
            <button 
              onClick={handleCollapse}
              className="text-sm bg-[#4A1D7A] hover:bg-[#5F259F] text-white py-2 px-3 rounded-lg transition-colors font-medium"
            >
              Minimise
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResultPanel; 